import { FetchResult } from "@apollo/client";
import { computed, ComputedRef, Ref, ref, unref, watch } from "vue";

import useNotifications from "@/hooks/notifications";

import { FeatureOperator, FeatureType } from "./generated-types";

export type AttributeValue = number | string | boolean | number[] | string[] | null | undefined;

interface UseMutationResponseOptions {
  shouldNotifyError: boolean;
  shouldNotifySuccess: boolean;
}

type UseMutationResponse = <T extends FetchResult | null, U extends { success: boolean; errorMessage: string }>(
  mutationResponse: T,
  getResultCallback: (data: NonNullable<T>["data"]) => U | undefined,
  options?: Partial<UseMutationResponseOptions>
) => U | null;

export const useMutationResponse: UseMutationResponse = (
  mutationResponse,
  getResultCallback,
  options = { shouldNotifyError: true, shouldNotifySuccess: true }
) => {
  if (!mutationResponse || mutationResponse == null) {
    return null;
  }
  const { data } = mutationResponse;
  if (data == null) {
    return null;
  }

  const { shouldNotifyError, shouldNotifySuccess } = {
    shouldNotifyError: true,
    shouldNotifySuccess: true,
    ...options,
  };

  const result = getResultCallback(data);

  if (result == null) {
    return null;
  }

  if (!result.success && result.errorMessage && shouldNotifyError) {
    const { notifyError } = useNotifications();
    notifyError(result.errorMessage, {
      dangerouslyUseHTMLString: true,
      duration: 15 * 1000,
    });
  }

  if (result.success && shouldNotifySuccess) {
    const { notifySuccess } = useNotifications();
    notifySuccess("Your operation was successful.", {
      duration: 5 * 1000,
    });
  }

  return result;
};

type UseQueryResult = <T, U>(queryResult: Ref<T>, getValueCallback: (data: T) => U) => ComputedRef<U>;

export const useQueryResult: UseQueryResult = (queryResult, getValueCallback) =>
  computed(() => getValueCallback(queryResult.value as never));

interface RelayConnection<T> {
  pageInfo: {
    hasNextPage: boolean;
    startCursor?: string | null;
  };
  edges: {
    node: T;
  }[];
}

export const useRelayConnection = <TNode, TQueryResult>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchMore: any,
  queryReturn: Ref<TQueryResult>,
  getConnectionCallback: (data: TQueryResult) => RelayConnection<TNode> | undefined | null
): {
  list: Ref<TNode[]>;
  loading: Ref<boolean>;
  hasNextPage: Ref<boolean>;
  loadMore: () => void;
} => {
  const fetchMoreLoading = ref(false);
  const currentConnection = ref<RelayConnection<TNode> | undefined>();
  const list: Ref<TNode[]> = ref([]);

  const parseConnection = (con?: RelayConnection<TNode> | Ref<RelayConnection<TNode> | undefined>): TNode[] => {
    if (con == null) {
      return [];
    }
    const unwrappedConnection = unref(con);
    if (unwrappedConnection == null) {
      return [];
    }
    return unwrappedConnection?.edges.map((o) => o.node) ?? [];
  };

  watch(queryReturn, (watchedQueryReturn) => {
    if (watchedQueryReturn) {
      currentConnection.value = getConnectionCallback(watchedQueryReturn) ?? undefined;
      const foo = parseConnection(currentConnection);
      list.value = [...foo];
    }
  });

  const hasNextPage = computed(() => currentConnection.value?.pageInfo.hasNextPage ?? false);
  const startCursor = computed(() => currentConnection.value?.pageInfo.startCursor);

  const loadMore = async () => {
    if (hasNextPage.value) {
      fetchMoreLoading.value = true;
      const response = await fetchMore({ variables: { after: startCursor.value as never } });
      fetchMoreLoading.value = false;
      if (response.data) {
        const newConnection = getConnectionCallback(response.data) ?? undefined;
        const items = parseConnection(newConnection);
        list.value = [...list.value, ...items];
        currentConnection.value = newConnection;
      }
    }
  };

  return {
    list,
    loading: fetchMoreLoading,
    loadMore,
    hasNextPage,
  };
};

export const featureOperatorLabels: Record<FeatureOperator, string> = {
  [FeatureOperator.GreaterThan]: "greater than",
  [FeatureOperator.GreaterThanOrEqual]: "greater than or equal",
  [FeatureOperator.LowerThan]: "lower than",
  [FeatureOperator.LowerThanOrEqual]: "lower than or equal",
  [FeatureOperator.Equals]: "equals",
  [FeatureOperator.NotEquals]: "not equals",
  [FeatureOperator.In]: "in",
  [FeatureOperator.NotIn]: "not in",
  [FeatureOperator.Contains]: "contains",
  [FeatureOperator.NotContains]: "not contains",
  [FeatureOperator.VersionGreaterThan]: "version greater than",
  [FeatureOperator.VersionGreaterThanOrEqual]: "version greater than or equal",
  [FeatureOperator.VersionLowerThan]: "version lower than",
  [FeatureOperator.VersionLowerThanOrEqual]: "version lower than or equal",
};

export const featureTypeLabel: Record<FeatureType, string> = {
  [FeatureType.String]: "text",
  [FeatureType.Boolean]: "boolean",
  [FeatureType.Integer]: "integer",
  [FeatureType.Float]: "float",
  [FeatureType.Select]: "select",
  [FeatureType.Version]: "version",
};

export const featureTypeChoices = Object.values(FeatureType).map((value) => ({
  label: featureTypeLabel[value],
  value,
}));
